// src/actions/shakerpro_orders_actions.js
//
// SHAKERPRO sales channel (shakerpro.barsys.com launch site).
// Backend is a live proxy over the shakerpro order feed:
//   /secureApi/analytics/shakerpro/...
// Response shapes mirror the US channel, so components reuse the same renderers.

import axios from "axios";
import {
  FETCH_SHAKERPRO_ORDERS_LOADING,
  FETCH_SHAKERPRO_ORDERS_SUCCESS,
  FETCH_SHAKERPRO_ORDERS_FAIL,
  FETCH_SHAKERPRO_ORDER_DETAILS_LOADING,
  FETCH_SHAKERPRO_ORDER_DETAILS_SUCCESS,
  FETCH_SHAKERPRO_ORDER_DETAILS_FAIL,
  FETCH_SHAKERPRO_ORDERS_COMPARISON_LOADING,
  FETCH_SHAKERPRO_ORDERS_COMPARISON_SUCCESS,
  FETCH_SHAKERPRO_ORDERS_COMPARISON_FAIL,
  FETCH_SHAKERPRO_PRODUCT_SALES_LOADING,
  FETCH_SHAKERPRO_PRODUCT_SALES_SUCCESS,
  FETCH_SHAKERPRO_PRODUCT_SALES_FAIL,
  FETCH_SHAKERPRO_PRODUCT_SALES_BY_MONTH_LOADING,
  FETCH_SHAKERPRO_PRODUCT_SALES_BY_MONTH_SUCCESS,
  FETCH_SHAKERPRO_PRODUCT_SALES_BY_MONTH_FAIL,
  FETCH_SHAKERPRO_PRODUCT_UNITS_LOADING,
  FETCH_SHAKERPRO_PRODUCT_UNITS_SUCCESS,
  FETCH_SHAKERPRO_PRODUCT_UNITS_FAIL,
  FETCH_SHAKERPRO_PRODUCT_UNITS_BY_MONTH_LOADING,
  FETCH_SHAKERPRO_PRODUCT_UNITS_BY_MONTH_SUCCESS,
  FETCH_SHAKERPRO_PRODUCT_UNITS_BY_MONTH_FAIL,
  FULFILL_SHAKERPRO_ORDER_LOADING,
  FULFILL_SHAKERPRO_ORDER_SUCCESS,
  FULFILL_SHAKERPRO_ORDER_FAIL,
} from "./types";

const BASE = () =>
  `${process.env.REACT_APP_ADMIN_API_ADDRESS}/secureApi/analytics/shakerpro`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
});

// ---------------------------------------------------------------------------
// Orders table (paginated) — GET /orders/paginated-by-range
// ---------------------------------------------------------------------------
export const fetchShakerproOrders =
  (page = 1, perPage = 20, searchTerm = "", range = "30 days") =>
  async (dispatch) => {
    dispatch({ type: FETCH_SHAKERPRO_ORDERS_LOADING });
    try {
      const resp = await axios.get(`${BASE()}/orders/paginated-by-range`, {
        headers: authHeaders(),
        params: {
          range,
          page,
          perPage,
          ...(searchTerm ? { searchTerm } : {}),
        },
      });
      if (resp.data?.success) {
        dispatch({
          type: FETCH_SHAKERPRO_ORDERS_SUCCESS,
          payload: {
            orders: resp.data.orders || [],
            totalOrders: resp.data.totalOrders || 0,
          },
        });
      } else {
        dispatch({
          type: FETCH_SHAKERPRO_ORDERS_FAIL,
          payload: resp.data?.message || "Failed to fetch Shaker Pro orders",
        });
      }
    } catch (err) {
      dispatch({
        type: FETCH_SHAKERPRO_ORDERS_FAIL,
        payload:
          err.response?.data?.message || err.message || "Failed to fetch Shaker Pro orders",
      });
    }
  };

// ---------------------------------------------------------------------------
// Comparison — GET /orders/comparison-by-range  (range: day|week|month|year)
// ---------------------------------------------------------------------------
export const fetchShakerproOrdersComparison =
  ({ range = "day" } = {}) =>
  async (dispatch) => {
    dispatch({ type: FETCH_SHAKERPRO_ORDERS_COMPARISON_LOADING });
    try {
      const resp = await axios.get(`${BASE()}/orders/comparison-by-range`, {
        headers: authHeaders(),
        params: { range },
      });
      if (resp.data?.success) {
        dispatch({
          type: FETCH_SHAKERPRO_ORDERS_COMPARISON_SUCCESS,
          payload: resp.data.data,
        });
      } else {
        dispatch({
          type: FETCH_SHAKERPRO_ORDERS_COMPARISON_FAIL,
          payload: resp.data?.message || "Failed to fetch comparison",
        });
      }
    } catch (err) {
      dispatch({
        type: FETCH_SHAKERPRO_ORDERS_COMPARISON_FAIL,
        payload: err.response?.data?.message || err.message || "Failed to fetch comparison",
      });
    }
  };

// ---------------------------------------------------------------------------
// Order details — GET /order-details/:orderId
// ---------------------------------------------------------------------------
export const fetchShakerproOrderDetails = (orderId) => async (dispatch) => {
  dispatch({ type: FETCH_SHAKERPRO_ORDER_DETAILS_LOADING });
  try {
    const resp = await axios.get(`${BASE()}/order-details/${orderId}`, {
      headers: authHeaders(),
    });
    if (resp.data?.success) {
      dispatch({ type: FETCH_SHAKERPRO_ORDER_DETAILS_SUCCESS, payload: resp.data.data });
    } else {
      dispatch({
        type: FETCH_SHAKERPRO_ORDER_DETAILS_FAIL,
        payload: resp.data?.message || "Failed to fetch order details",
      });
    }
  } catch (err) {
    dispatch({
      type: FETCH_SHAKERPRO_ORDER_DETAILS_FAIL,
      payload: err.response?.data?.message || err.message || "Failed to fetch order details",
    });
  }
};

// ---------------------------------------------------------------------------
// Product sales (daily) — GET /orders/product-sales?range=7d|30d|...
// ---------------------------------------------------------------------------
export const fetchShakerproProductSales =
  (range = "7d") =>
  async (dispatch) => {
    dispatch({ type: FETCH_SHAKERPRO_PRODUCT_SALES_LOADING });
    try {
      const resp = await axios.get(`${BASE()}/orders/product-sales`, {
        headers: authHeaders(),
        params: { range },
      });
      if (resp.data?.success) {
        dispatch({ type: FETCH_SHAKERPRO_PRODUCT_SALES_SUCCESS, payload: resp.data.data });
      } else {
        dispatch({
          type: FETCH_SHAKERPRO_PRODUCT_SALES_FAIL,
          payload: resp.data?.message || "Failed to fetch product sales",
        });
      }
    } catch (err) {
      dispatch({
        type: FETCH_SHAKERPRO_PRODUCT_SALES_FAIL,
        payload: err.response?.data?.message || err.message || "Failed to fetch product sales",
      });
    }
  };

// ---------------------------------------------------------------------------
// Product sales by month — GET /orders/product-sales-by-month?year=YYYY
// ---------------------------------------------------------------------------
export const fetchShakerproProductSalesByMonth = (year) => async (dispatch) => {
  dispatch({ type: FETCH_SHAKERPRO_PRODUCT_SALES_BY_MONTH_LOADING });
  try {
    const resp = await axios.get(`${BASE()}/orders/product-sales-by-month`, {
      headers: authHeaders(),
      params: { year },
    });
    if (resp.data?.success) {
      dispatch({
        type: FETCH_SHAKERPRO_PRODUCT_SALES_BY_MONTH_SUCCESS,
        payload: resp.data.data,
      });
    } else {
      dispatch({
        type: FETCH_SHAKERPRO_PRODUCT_SALES_BY_MONTH_FAIL,
        payload: resp.data?.message || "Failed to fetch monthly product sales",
      });
    }
  } catch (err) {
    dispatch({
      type: FETCH_SHAKERPRO_PRODUCT_SALES_BY_MONTH_FAIL,
      payload:
        err.response?.data?.message || err.message || "Failed to fetch monthly product sales",
    });
  }
};

// ---------------------------------------------------------------------------
// Product units (daily) — GET /orders/product-units-per-day?range=7 days|14 days|month
// ---------------------------------------------------------------------------
export const fetchShakerproProductUnits =
  (range = "7 days") =>
  async (dispatch) => {
    dispatch({ type: FETCH_SHAKERPRO_PRODUCT_UNITS_LOADING });
    try {
      const resp = await axios.get(`${BASE()}/orders/product-units-per-day`, {
        headers: authHeaders(),
        params: { range },
      });
      if (resp.data?.success) {
        dispatch({ type: FETCH_SHAKERPRO_PRODUCT_UNITS_SUCCESS, payload: resp.data.data });
      } else {
        dispatch({
          type: FETCH_SHAKERPRO_PRODUCT_UNITS_FAIL,
          payload: resp.data?.message || "Failed to fetch product units",
        });
      }
    } catch (err) {
      dispatch({
        type: FETCH_SHAKERPRO_PRODUCT_UNITS_FAIL,
        payload: err.response?.data?.message || err.message || "Failed to fetch product units",
      });
    }
  };

// ---------------------------------------------------------------------------
// Product units by month — GET /orders/product-units-by-month?year=YYYY
// ---------------------------------------------------------------------------
export const fetchShakerproProductUnitsByMonth = (year) => async (dispatch) => {
  dispatch({ type: FETCH_SHAKERPRO_PRODUCT_UNITS_BY_MONTH_LOADING });
  try {
    const resp = await axios.get(`${BASE()}/orders/product-units-by-month`, {
      headers: authHeaders(),
      params: { year },
    });
    if (resp.data?.success) {
      dispatch({
        type: FETCH_SHAKERPRO_PRODUCT_UNITS_BY_MONTH_SUCCESS,
        payload: resp.data.data,
      });
    } else {
      dispatch({
        type: FETCH_SHAKERPRO_PRODUCT_UNITS_BY_MONTH_FAIL,
        payload: resp.data?.message || "Failed to fetch monthly product units",
      });
    }
  } catch (err) {
    dispatch({
      type: FETCH_SHAKERPRO_PRODUCT_UNITS_BY_MONTH_FAIL,
      payload:
        err.response?.data?.message || err.message || "Failed to fetch monthly product units",
    });
  }
};

// ---------------------------------------------------------------------------
// Fulfillment write-back — POST /orders/fulfill { orderId, tracking, carrier }
// On success the caller should re-fetch the orders table.
// ---------------------------------------------------------------------------
export const fulfillShakerproOrder =
  ({ orderId, tracking, carrier }, onDone) =>
  async (dispatch) => {
    dispatch({ type: FULFILL_SHAKERPRO_ORDER_LOADING, payload: { orderId } });
    try {
      const resp = await axios.post(
        `${BASE()}/orders/fulfill`,
        { orderId, tracking, carrier },
        { headers: authHeaders() }
      );
      if (resp.data?.success) {
        dispatch({
          type: FULFILL_SHAKERPRO_ORDER_SUCCESS,
          payload: resp.data.data?.order || { orderId, tracking, carrier },
        });
        if (typeof onDone === "function") onDone(null, resp.data.data?.order);
      } else {
        const msg = resp.data?.message || "Failed to record fulfillment";
        dispatch({ type: FULFILL_SHAKERPRO_ORDER_FAIL, payload: msg });
        if (typeof onDone === "function") onDone(msg);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Failed to record fulfillment";
      dispatch({ type: FULFILL_SHAKERPRO_ORDER_FAIL, payload: msg });
      if (typeof onDone === "function") onDone(msg);
    }
  };

// ---------------------------------------------------------------------------
// SALEOR_US (barsys.com USD web store) — Mark shipped.
// Creates the fulfillment in Saleor (customer email + tracking) and pushes the
// tracking number to PayPal. Reuses the shakerpro fulfill redux plumbing: the
// reducer merges the returned row into the merged US `orders` list by orderId.
// ---------------------------------------------------------------------------
export const fulfillSaleorUsOrder =
  ({ orderId, tracking, carrier }, onDone) =>
  async (dispatch) => {
    dispatch({ type: FULFILL_SHAKERPRO_ORDER_LOADING, payload: { orderId } });
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_ADMIN_API_ADDRESS}/secureApi/analytics/saleorus/orders/fulfill`,
        { orderId, tracking, carrier },
        { headers: authHeaders() }
      );
      if (resp.data?.success) {
        const data = resp.data.data || { orderId, tracking, carrier, fulfillmentStatus: "Fulfilled" };
        dispatch({ type: FULFILL_SHAKERPRO_ORDER_SUCCESS, payload: data });
        if (typeof onDone === "function") onDone(null, data);
      } else {
        const msg = resp.data?.message || "Failed to record fulfillment";
        dispatch({ type: FULFILL_SHAKERPRO_ORDER_FAIL, payload: msg });
        if (typeof onDone === "function") onDone(msg);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Failed to record fulfillment";
      dispatch({ type: FULFILL_SHAKERPRO_ORDER_FAIL, payload: msg });
      if (typeof onDone === "function") onDone(msg);
    }
  };
